import React from "react";
import { motion } from "framer-motion";
import { Cpu, Rocket, Code2, GitBranch } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { METHODOLOGIES, IMAGES } from "../data/content";

const services = [
  { icon: Cpu, tKey: "service_1", span: "lg:col-span-7" },
  { icon: Rocket, tKey: "service_2", span: "lg:col-span-5" },
  { icon: Code2, tKey: "service_3", span: "lg:col-span-5" },
  { icon: GitBranch, tKey: "service_4", span: "lg:col-span-7" },
];

const SectionHead = ({ eyebrow, title, subtitle, testid }) => (
  <div className="max-w-3xl mb-14" data-testid={testid}>
    <div className="text-xs tracking-[0.2em] uppercase font-mono text-cyan mb-4">{eyebrow}</div>
    <h2 className="font-head font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">{title}</h2>
    <p className="mt-5 text-base text-zinc-400 leading-relaxed">{subtitle}</p>
  </div>
);

export const Services = () => {
  const { t } = useLang();
  return (
    <section id="services" className="relative py-24 lg:py-32 overflow-hidden" data-testid="services-section">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-16">
          <div className="min-w-0">
            <SectionHead eyebrow={t("services_eyebrow")} title={t("services_title")} subtitle={t("services_subtitle")} testid="services-head" />
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-2xl overflow-hidden border border-[#00F0FF]/25 shadow-[0_12px_48px_rgba(0,240,255,0.12)]"
            data-testid="services-visual"
          >
            <img src="/pm-timeline.jpg" alt="Project planning timeline" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#030305]/50 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 font-mono text-xs text-cyan bg-black/60 px-3 py-1.5 rounded-md border border-[#00F0FF]/20">
              PROJECT_TIMELINE · LIVE
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.tKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`card-glow rounded-2xl p-8 ${s.span}`}
                data-testid={`service-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center mb-6">
                  <Icon size={22} className="text-cyan" />
                </div>
                <h3 className="font-head font-semibold text-xl sm:text-2xl text-white mb-3">{t(`${s.tKey}_title`)}</h3>
                <p className="text-zinc-400 leading-relaxed">{t(`${s.tKey}_desc`)}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Methodology marquee */}
        <div className="mt-16 relative overflow-hidden border-y border-white/10 py-6" data-testid="methodology-marquee">
          <div className="flex marquee-track gap-4 w-max">
            {[...METHODOLOGIES, ...METHODOLOGIES].map((m, i) => (
              <span key={i} className="px-5 py-2 rounded-full border border-white/10 text-sm font-mono text-zinc-400 whitespace-nowrap">
                {m}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export { SectionHead };
